/* eslint-disable */

import { indian__currency__rounder } from '../../utils/CurrencyUtil'
import {
  displayText,
  formatDisplayDate,
  formatRupee,
  getAgeFromDob,
  isBlank,
  parseAmount,
} from './investorProfileUtils'
import { calculateFirstPass, restorePlanner } from '../FirstIdeas/questionnaire2Model'

export const ADMIN_BASE_PATH =
  '/6fb2a4ac30ddccb8b418e27864a778dee5409a7da4c55660f49f21b8543289b4/admin'

export const firstUserIdeaDetailPath = (phoneNumber) =>
  `${ADMIN_BASE_PATH}/firstUserIdeas/${encodeURIComponent(phoneNumber)}`

export const CHART_COLORS = {
  income: 'var(--fb-accent)',
  expenses: 'var(--fb-vulcan-400)',
  surplus: 'var(--fb-lime-500)',
  assets: 'var(--fb-blue-500)',
  goals: 'var(--fb-lime-700)',
  current: 'var(--fb-blue-300)',
  retirement: 'var(--fb-blue-700)',
  slices: [
    'var(--fb-accent)',
    'var(--fb-blue-500)',
    'var(--fb-blue-300)',
    'var(--fb-lime-500)',
    'var(--fb-lime-700)',
    'var(--fb-vulcan-500)',
    'var(--fb-vulcan-400)',
    'var(--fb-vulcan-300)',
  ],
}

const MONTHLY_EXPENSE_LABELS = {
  emi: 'EMI',
  rent: 'Rent',
  parents: 'Parents support',
  children: 'Children',
  household: 'Household',
}

const YEARLY_EXPENSE_LABELS = {
  vacation: 'Vacation',
  lifestyle: 'Lifestyle',
  termPremium: 'Term premium',
  carInsurance: 'Car insurance',
  childrenFees: 'Children fees',
  medicalPremium: 'Medical premium',
  otherInsurance: 'Other insurance',
}

const INSURANCE_LABELS = {
  term: 'Term insurance',
  privateMedical: 'Private medical',
  corporateMedical: 'Corporate medical',
}

export const removeCountryCode = (phoneNumber = '') => String(phoneNumber).replace(/^\+91/, '')

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isVersion2Record = (value) => {
  if (!isPlainObject(value)) return false
  const version = Number(value.version ?? value.questionnaire2Version)
  if (version === 2) return true
  return Boolean(value.monthlyExpenses && (value.members || value.salaries || value.assetGoals))
}

const parseMaybeJson = (value) => {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (!trimmed) return value
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

const deepParse = (value, depth = 0) => {
  if (depth > 4) return value
  if (typeof value === 'string') {
    const parsed = parseMaybeJson(value)
    if (parsed !== value) return deepParse(parsed, depth + 1)
    return parsed
  }
  return value
}

const isFullV2Record = (value) =>
  isVersion2Record(value) &&
  Boolean(value.members || value.monthlyExpenses || value.salaries || value.holdings)

const mergeResponseArray = (items) => {
  const merged = {}
  items.forEach((item) => {
    const parsed = deepParse(item)
    if (isPlainObject(parsed)) {
      Object.entries(parsed).forEach(([key, value]) => {
        merged[key] = deepParse(value)
      })
      return
    }
    if (isVersion2Record(parsed)) Object.assign(merged, parsed)
  })
  return merged
}

export const unwrapUserResponse = (userResponse) => {
  let data = deepParse(userResponse)
  if (isPlainObject(data) && data.userResponse !== undefined) {
    data = deepParse(data.userResponse)
  }
  if (Array.isArray(data)) {
    const parsedItems = data.map((item) => deepParse(item))
    const fullRecord = parsedItems.find((item) => isFullV2Record(item))
    if (fullRecord) return fullRecord

    const merged = mergeResponseArray(parsedItems)
    if (isVersion2Record(merged)) return merged

    const nested = Object.values(merged).find((value) => isFullV2Record(deepParse(value)))
    if (nested) return deepParse(nested)

    return parsedItems
  }
  return data
}

export const isQuestionnaireV2 = (userResponse) =>
  isVersion2Record(unwrapUserResponse(userResponse))

export const getQuestionnaireVersion = (userResponse, fallbackRecord) => {
  const sources = [unwrapUserResponse(userResponse), fallbackRecord]
  for (const source of sources) {
    if (typeof source === 'number' && source > 0) return source
    if (!isPlainObject(source)) continue
    const version = Number(source.version ?? source.questionnaire2Version)
    if (Number.isFinite(version) && version > 0) return version
    if (isVersion2Record(source)) return 2
  }
  return 1
}

const sumAmounts = (values) =>
  values.reduce((total, value) => {
    const amount = parseAmount(value)
    return amount === null ? total : total + amount
  }, 0)

const objectAmountRows = (source, labels, selectedKeys = []) =>
  Object.entries(labels)
    .map(([key, label]) => {
      const amount = parseAmount(source?.[key])
      const selected = selectedKeys.includes(key)
      if ((amount === null || amount === 0) && !selected) return null
      return {
        key,
        label,
        amount: amount || 0,
        selected,
        missing: selected && !amount,
      }
    })
    .filter(Boolean)

const whoLabel = (value) => {
  if (isBlank(value)) return null
  const key = String(value).trim().toLowerCase()
  if (key === 'me' || key === 'self') return 'Me'
  return humanize(value)
}

const isYes = (value) =>
  String(value || '')
    .trim()
    .toLowerCase() === 'yes'

const isNo = (value) =>
  String(value || '')
    .trim()
    .toLowerCase() === 'no'

const humanize = (value) =>
  String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase())

const compactRupee = (value) => {
  const amount = parseAmount(value)
  if (amount === null) return '₹0'
  return `₹${indian__currency__rounder(amount)}`
}

const percent = (value) => `${Math.round(value)}%`

const extraExpenseAmount = (item) => {
  if (!item || typeof item !== 'object') return parseAmount(item)
  return parseAmount(item.amount ?? item.value ?? item.cost ?? item.yearly)
}

const extraExpenseName = (item, index) => {
  if (!item || typeof item !== 'object') return `Extra expense ${index + 1}`
  return item.name || item.label || item.title || `Extra expense ${index + 1}`
}

const loanAmount = (loan) =>
  parseAmount(loan?.outstanding ?? loan?.balance ?? loan?.amount ?? loan?.principal)

const loanEmi = (loan) => parseAmount(loan?.emi ?? loan?.monthly ?? loan?.instalment)

const loanRemark = (loan) => {
  if (!loan || typeof loan !== 'object') return null
  const value = loan.remark ?? loan.remarks ?? loan.note ?? loan.notes ?? loan.details
  if (isBlank(value)) return null
  return String(value).trim()
}

export const buildQuestionnaireV2View = ({ data, selectedUser, singleUser }) => {
  const profile = isPlainObject(data) ? data : {}
  const members = Array.isArray(profile.members) ? profile.members : []
  const salaries = Array.isArray(profile.salaries) ? profile.salaries : []
  const holdings = Array.isArray(profile.holdings) ? profile.holdings : []
  const assetGoals = Array.isArray(profile.assetGoals) ? profile.assetGoals : []
  const loans = Array.isArray(profile.loans) ? profile.loans : []
  const extraYearlyExpenses = Array.isArray(profile.extraYearlyExpenses)
    ? profile.extraYearlyExpenses
    : []
  const insurance = isPlainObject(profile.insurance) ? profile.insurance : {}
  const retirement = isPlainObject(profile.retirement) ? profile.retirement : {}
  const assumptions = isPlainObject(profile.assumptions) ? profile.assumptions : {}
  const monthlySource = isPlainObject(profile.monthlyExpenses) ? profile.monthlyExpenses : {}
  const yearlySource = isPlainObject(profile.yearlyExpenses) ? profile.yearlyExpenses : {}

  const annualSelected = Array.isArray(profile.annualSelected) ? profile.annualSelected : []
  const memberNameById = members.reduce((acc, member) => {
    if (member?.id) acc[member.id] = member.name
    return acc
  }, {})

  const monthlyIncome = sumAmounts(salaries.map((item) => item?.gross))
  const monthlyExpenseRows = objectAmountRows(monthlySource, MONTHLY_EXPENSE_LABELS)
  const yearlyExpenseRows = [
    ...objectAmountRows(yearlySource, YEARLY_EXPENSE_LABELS, annualSelected),
    ...extraYearlyExpenses
      .map((item, index) => {
        const amount = extraExpenseAmount(item)
        return amount
          ? {
            key: item.id || `extra-${index}`,
            label: extraExpenseName(item, index),
            amount,
            extra: true,
          }
          : null
      })
      .filter(Boolean),
  ]

  const monthlyExpensesTotal = sumAmounts(monthlyExpenseRows.map((row) => row.amount))
  const yearlyExpensesTotal = sumAmounts(yearlyExpenseRows.map((row) => row.amount))
  const yearlyAsMonthly = yearlyExpensesTotal / 12
  const totalMonthlyOutflow = monthlyExpensesTotal + yearlyAsMonthly
  const surplus = monthlyIncome - totalMonthlyOutflow
  const savingsRate = monthlyIncome > 0 ? (surplus / monthlyIncome) * 100 : null
  const holdingsTotal = sumAmounts(holdings.map((item) => item?.value))
  const goalsTotal = sumAmounts(assetGoals.map((item) => item?.amount))
  const goalsSaved = sumAmounts(assetGoals.map((item) => item?.saved))
  const loansOutstanding = sumAmounts(loans.map((loan) => loanAmount(loan)))
  const netWorth = holdingsTotal - loansOutstanding
  const goalCoverage = goalsTotal > 0 ? (holdingsTotal / goalsTotal) * 100 : null

  const memberRows = members.map((member) => ({
    ...member,
    age: getAgeFromDob(member?.dob),
    dobLabel: formatDisplayDate(member?.dob),
    dependent: isYes(member?.dependent),
  }))
  const self = memberRows.find(
    (member) => String(member.relationship || '').toLowerCase() === 'self',
  )

  const expenseChart = [
    ...monthlyExpenseRows,
    ...yearlyExpenseRows.map((row) => ({
      ...row,
      amount: row.amount / 12,
      label: `${row.label} (monthly)`,
    })),
  ]
    .filter((row) => row.amount > 0)
    .map((row, index) => ({
      name: row.label,
      amount: Math.round(row.amount),
      fill: CHART_COLORS.slices[index % CHART_COLORS.slices.length],
    }))

  const cashflowChart = [
    { name: 'Income', amount: Math.max(monthlyIncome, 0), fill: CHART_COLORS.income },
    { name: 'Expenses', amount: Math.max(totalMonthlyOutflow, 0), fill: CHART_COLORS.expenses },
    {
      name: surplus >= 0 ? 'Surplus' : 'Shortfall',
      amount: Math.abs(surplus),
      fill: surplus >= 0 ? CHART_COLORS.surplus : 'var(--fb-danger)',
    },
  ].filter((row) => row.amount > 0)

  const assetsChart = [
    { name: 'Holdings', amount: holdingsTotal, fill: CHART_COLORS.assets },
    { name: 'Saved for goals', amount: goalsSaved, fill: CHART_COLORS.surplus },
    { name: 'Goal target', amount: goalsTotal, fill: CHART_COLORS.goals },
  ].filter((row) => row.amount > 0)

  const retirementMonthly =
    parseAmount(retirement.monthly) || (parseAmount(retirement.annual) || 0) / 12
  const retirementChart =
    retirementMonthly > 0 || totalMonthlyOutflow > 0
      ? [
        {
          name: 'Today',
          amount: Math.round(totalMonthlyOutflow),
          fill: CHART_COLORS.current,
        },
        {
          name: 'Retirement',
          amount: Math.round(retirementMonthly),
          fill: CHART_COLORS.retirement,
        },
      ].filter((row) => row.amount > 0)
      : []

  const insuranceItems = [
    {
      key: 'term',
      label: INSURANCE_LABELS.term,
      covered: isYes(insurance.term),
      declined: isNo(insurance.term),
      who: whoLabel(insurance.termWho),
      coverage: insurance.termCoverage,
      coverageAmount: parseAmount(insurance.termCoverage),
      basis: insurance.termBasis || null,
    },
    {
      key: 'privateMedical',
      label: INSURANCE_LABELS.privateMedical,
      covered: isYes(insurance.privateMedical),
      declined: isNo(insurance.privateMedical),
      who: whoLabel(insurance.privateWho),
      coverage: insurance.privateCoverage,
      coverageAmount: parseAmount(insurance.privateCoverage),
      basis: insurance.privateBasis || null,
    },
    {
      key: 'corporateMedical',
      label: INSURANCE_LABELS.corporateMedical,
      covered: isYes(insurance.corporateMedical),
      declined: isNo(insurance.corporateMedical),
      who: whoLabel(insurance.corporateWho),
      coverage: insurance.corporateCoverage,
      coverageAmount: parseAmount(insurance.corporateCoverage),
      basis: insurance.corporateBasis || null,
    },
  ]

  const currentYear = new Date().getFullYear()
  const goalRows = assetGoals.map((goal) => {
    const amount = parseAmount(goal?.amount)
    const saved = parseAmount(goal?.saved) || 0
    const year = parseAmount(goal?.year)
    const remaining = amount !== null ? Math.max(amount - saved, 0) : null
    return {
      ...goal,
      amount,
      saved,
      remaining,
      yearsAway: year ? Math.max(year - currentYear, 0) : null,
      fundedPercent: amount ? Math.min((saved / amount) * 100, 100) : null,
    }
  })

  const fullName =
    `${selectedUser?.firstName || singleUser?.firstName || ''} ${selectedUser?.lastName || singleUser?.lastName || ''
      }`.trim() ||
    self?.name ||
    'Investor'

  return {
    fullName,
    phoneNumber: selectedUser?.phoneNumber || singleUser?.phoneNumber || null,
    isCompleted: Boolean(selectedUser?.isCompleted ?? singleUser?.isCompleted),
    submittedOn: selectedUser?.createdTimestamp || singleUser?.createdTimestamp,
    updatedOn: selectedUser?.updatedTimestamp || singleUser?.updatedTimestamp,
    version: getQuestionnaireVersion(profile),
    step: profile.questionnaire2Step || null,
    housing: monthlySource.housing || null,
    parentsSupport: isBlank(profile.parentsSupport) ? null : displayText(profile.parentsSupport),
    annualChoice: profile.annualChoice || null,
    annualSelected: annualSelected.map((key) => YEARLY_EXPENSE_LABELS[key] || humanize(key)),
    lifestyleConfirmed: Boolean(profile.lifestyleConfirmed),
    metrics: [
      { label: 'Monthly income', value: formatRupee(monthlyIncome) },
      { label: 'Monthly outgo', value: formatRupee(totalMonthlyOutflow) },
      {
        label: surplus >= 0 ? 'Monthly surplus' : 'Monthly shortfall',
        value: formatRupee(Math.abs(surplus)),
      },
      { label: 'Savings rate', value: savingsRate !== null ? percent(savingsRate) : null },
      { label: 'Net assets', value: formatRupee(netWorth) },
      { label: 'Goal coverage', value: goalCoverage !== null ? percent(goalCoverage) : null },
    ],
    cashflowChart,
    expenseChart,
    assetsChart,
    retirementChart,
    members: memberRows,
    salaries: salaries.map((salary) => ({
      ...salary,
      grossAmount: parseAmount(salary?.gross),
      pfAmount: parseAmount(salary?.pf),
      npsAmount: parseAmount(salary?.nps),
      taxAmount: parseAmount(salary?.tax),
      memberName: memberNameById[salary?.member] || salary?.member || null,
    })),
    monthlyExpenseRows,
    yearlyExpenseRows,
    extraYearlyExpenses,
    holdings: holdings.map((holding) => ({
      ...holding,
      valueAmount: parseAmount(holding?.value),
    })),
    goals: goalRows,
    loans: loans.map((loan) => ({
      ...loan,
      outstandingAmount: loanAmount(loan),
      emiAmount: loanEmi(loan),
      remark: loanRemark(loan),
    })),
    insuranceItems,
    retirement: {
      style: retirement.style || null,
      monthly: retirementMonthly || null,
      annual: parseAmount(retirement.annual),
      vacation: parseAmount(retirement.vacation),
      annualIncrease: retirement.annualIncrease,
      monthlyIncrease: retirement.monthlyIncrease,
      vacationIncrease: retirement.vacationIncrease,
      estimateConfirmed: Boolean(retirement.estimateConfirmed),
    },
    assumptions: Object.entries(assumptions).map(([key, value]) => ({
      key,
      label: humanize(key),
      value:
        typeof value === 'number'
          ? String(key).toLowerCase().includes('till')
            ? value
            : `${value}%`
          : displayText(value),
    })),
    totals: {
      monthlyIncome,
      monthlyExpensesTotal,
      yearlyExpensesTotal,
      yearlyAsMonthly,
      totalMonthlyOutflow,
      surplus,
      savingsRate,
      holdingsTotal,
      goalsTotal,
      goalsSaved,
      loansOutstanding,
      selfAge: self?.age ?? null,
      planTill: parseAmount(assumptions.planTill),
    },
    estimate: calculateFirstPass(
      restorePlanner({
        ...profile,
        questionnaire2Version: profile.questionnaire2Version ?? profile.version ?? 2,
      }),
    ),
  }
}

export const buildQuestionnaireV2Report = (view) => {
  const lines = [
    view.fullName,
    'Questionnaire v2 profile',
    `Status: ${view.isCompleted ? 'Completed' : 'Pending'}`,
    `Submitted: ${formatDisplayDate(view.submittedOn) || 'Not provided'}`,
    `Last updated: ${formatDisplayDate(view.updatedOn) || 'Not provided'}`,
    '',
    'Snapshot',
    ...view.metrics.map((metric) => `${metric.label}: ${metric.value || 'Not provided'}`),
    '',
    'Family',
    view.members.length
      ? view.members
        .map(
          (member) =>
            `- ${member.name || 'Member'} · ${member.relationship || '—'} · ${member.age !== null && member.age !== undefined ? `${member.age} yrs` : 'Age n/a'
            }`,
        )
        .join('\n')
      : 'Not provided',
    '',
    'Income',
    view.salaries.length
      ? view.salaries
        .map(
          (salary) =>
            `- ${salary.incomeType || 'Income'}: ${formatRupee(salary.grossAmount) || '—'}`,
        )
        .join('\n')
      : 'Not provided',
    '',
    'Spending',
    ...view.monthlyExpenseRows.map((row) => `${row.label}: ${formatRupee(row.amount)} / month`),
    ...view.yearlyExpenseRows.map((row) => `${row.label}: ${formatRupee(row.amount)} / year`),
    '',
    'Holdings',
    view.holdings.length
      ? view.holdings
        .map(
          (holding) =>
            `- ${holding.name || 'Holding'}: ${formatRupee(holding.valueAmount) || '—'}`,
        )
        .join('\n')
      : 'Not provided',
    '',
    'Goals',
    view.goals.length
      ? view.goals
        .map(
          (goal) =>
            `- ${goal.name || goal.kind || 'Goal'}: ${formatRupee(goal.amount) || '—'} (${goal.year || 'year n/a'
            })`,
        )
        .join('\n')
      : 'Not provided',
    '',
    'Gap analysis',
    `Estimated target today: ${formatRupee(view.estimate?.totalRequiredToday) || '₹0'}`,
    `Current net worth: ${formatRupee(view.estimate?.currentNetWorth) || '₹0'}`,
    `Remaining gap: ${formatRupee(view.estimate?.gapToday) || '₹0'}`,
    `Inflated gap by then: ${formatRupee(view.estimate?.futureGapTarget) || '₹0'}`,
    `Monthly surplus: ${formatRupee(view.estimate?.monthlySurplus) || '₹0'}`,
    `Inflation on requirement: ${view.estimate?.assumptions?.essentialInflation ?? '—'}%`,
    `Assumed yearly return: ${view.estimate?.assumptions?.gapInvestmentReturn ?? '—'}%`,
    `Time to close gap: ${formatGapYears(view.estimate?.gapClosureYears)}`,
    `When this happens: ${formatGapClosureMoment(view.estimate) || 'Needs monthly surplus'}`,
  ]

  return lines.join('\n')
}

export const formatGapYears = (years) => {
  if (years === null || years === undefined || !Number.isFinite(years)) return 'Needs monthly surplus'
  if (years <= 0) return 'Already covered'
  if (years < 1) {
    const months = Math.max(1, Math.ceil(years * 12))
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }
  const rounded = Math.round(years * 10) / 10
  return `${rounded.toLocaleString('en-IN', { maximumFractionDigits: 1 })} ${
    rounded === 1 ? 'year' : 'years'
  }`
}

export const formatGapClosureMoment = (estimate) => {
  if (!estimate?.gapClosureMonth || !Number.isFinite(estimate.gapClosureAge)) return null
  return `${estimate.gapClosureMonth}; they’ll be ${Math.round(estimate.gapClosureAge)} years old`
}

export const flattenForCsv = (value, prefix = '') => {
  const rows = []
  if (value === null || value === undefined || typeof value !== 'object') {
    rows.push([prefix || 'value', value ?? ''])
    return rows
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      rows.push([prefix, ''])
      return rows
    }
    value.forEach((item, index) => {
      const label = prefix ? `${prefix}[${index}]` : `[${index}]`
      if (item !== null && typeof item === 'object') {
        rows.push(...flattenForCsv(item, label))
      } else {
        rows.push([label, item ?? ''])
      }
    })
    return rows
  }

  Object.entries(value).forEach(([key, nested]) => {
    const label = prefix ? `${prefix}.${key}` : key
    if (nested !== null && typeof nested === 'object') {
      rows.push(...flattenForCsv(nested, label))
    } else {
      rows.push([label, nested ?? ''])
    }
  })
  return rows
}

export const downloadBlob = (content, filename, type = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const downloadUserResponseCsv = ({ selectedInvoice, userRes, users, singleUser }) => {
  const headers = ['Field', 'Value']
  const rows = [
    ['ID', selectedInvoice],
    ['Phone Number', selectedInvoice],
    [
      'Is Completed',
      users.find((user) => user.phoneNumber === selectedInvoice)?.isCompleted ||
        singleUser?.isCompleted
        ? 'Yes'
        : 'No',
    ],
    ['Created Timestamp', singleUser?.createdTimestamp || ''],
    ['Updated Timestamp', singleUser?.updatedTimestamp || ''],
  ]

  const payload = unwrapUserResponse(userRes)
  if (isQuestionnaireV2(payload)) {
    rows.push(...flattenForCsv(payload))
  } else if (Array.isArray(payload)) {
    payload.forEach((response) => {
      Object.entries(response || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) rows.push([key, value.join(', ')])
        else if (value && typeof value === 'object') rows.push([key, JSON.stringify(value)])
        else rows.push([key, value])
      })
    })
  } else if (payload && typeof payload === 'object') {
    rows.push(...flattenForCsv(payload))
  }

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','),
    ),
  ].join('\n')
  downloadBlob(csvContent, 'user_details.csv')
}

export const formatCompactRupee = compactRupee
export { formatRupee, formatDisplayDate, displayText }
